import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { NAV_SECTIONS } from "../lib/navigation";
import { HUB_CONTENT } from "../lib/content";
import { Reveal } from "../components/site/Reveal";

export default function SectionHub() {
  const { pathname } = useLocation();
  const section = NAV_SECTIONS.find((s) => s.path === pathname);

  if (!section) {
    return (
      <div className="pt-[140px] pb-24 container-rozier">
        <p className="font-serif text-3xl text-charcoal">Section introuvable.</p>
      </div>
    );
  }

  return (
    <div data-testid={`page-hub-${section.key}`}>
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[520px] overflow-hidden">
        <img
          src={section.image}
          alt={section.label}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/70" />
        <div className="absolute inset-0 flex items-end">
          <div className="container-rozier pb-16 md:pb-20 w-full">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-white/80 hover:text-gold transition-colors text-xs uppercase tracking-[0.28em] mb-6"
              data-testid="hub-back-home"
            >
              <ArrowLeft size={14} />
              Accueil
            </Link>
            <p className="overline text-white/70 mb-4 sr-only">Cœur de métier</p>
            <h1 className="font-serif text-white text-5xl md:text-7xl lg:text-8xl leading-[1.02] tracking-tight font-light">
              {section.label}
            </h1>
            <p className="mt-6 font-serif italic text-white/85 text-2xl md:text-3xl max-w-3xl leading-snug">
              {section.intro}
            </p>
          </div>
        </div>
      </section>

      {/* Hub intro (if provided) */}
      {HUB_CONTENT[section.key] && (
        <section className="py-20 md:py-28 bg-ivory border-b border-stone" data-testid={`hub-intro-${section.key}`}>
          <div className="container-rozier grid grid-cols-12 gap-10">
            <Reveal className="col-span-12 md:col-span-5">
              <p className="overline mb-6">{HUB_CONTENT[section.key].overline}</p>
              <h2 className="font-serif text-3xl md:text-5xl text-charcoal leading-[1.05] tracking-tight font-light">
                {HUB_CONTENT[section.key].tagline}
              </h2>
            </Reveal>
            <Reveal className="col-span-12 md:col-span-6 md:col-start-7" delay={150}>
              <div className="space-y-6">
                {HUB_CONTENT[section.key].paragraphs.map((p, i) => (
                  <p key={i} className="text-graphite text-base md:text-lg leading-[1.9]">
                    {p}
                  </p>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Items grid */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container-rozier">
          <Reveal>
            <p className="overline mb-6">Nos domaines</p>
            <h2 className="font-serif text-3xl md:text-5xl text-charcoal leading-tight font-light max-w-3xl">
              Explorer les <span className="italic gold-text">spécialités</span>.
            </h2>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-stone">
            {section.items.map((item, index) => (
              <Link
                key={item.key}
                to={item.path}
                className="group flex flex-col justify-between h-full p-8 md:p-10 bg-white hover:bg-ivory transition-colors duration-500 min-h-[260px]"
                data-testid={`hub-item-${item.key}`}
              >
                <div>
                  <p className="text-[10px] uppercase tracking-[0.32em] text-gold mb-4">
                    0{index + 1}
                  </p>
                  <h3 className="font-serif text-2xl md:text-3xl text-charcoal group-hover:text-gold transition-colors leading-tight">
                    {item.label}
                  </h3>
                  <p className="mt-4 text-graphite text-sm leading-relaxed">
                    {item.teaser}
                  </p>
                </div>
                <div className="mt-8 inline-flex items-center gap-2 text-charcoal group-hover:text-gold transition-colors">
                  <span className="text-[11px] uppercase tracking-[0.28em]">
                    Découvrir
                  </span>
                  <ArrowUpRight
                    size={14}
                    className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </div>
              </Link>
            ))}
            {Array.from({
              length: (3 - (section.items.length % 3)) % 3
            }).map((_, i) => (
              <div
                key={`filler-${i}`}
                aria-hidden="true"
                className="hidden lg:block bg-white min-h-[260px]"
                data-testid={`hub-filler-${i}`}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
